import { useEffect } from "react";

/**
 * Imperative SEO hook — sets document.title, meta description, canonical, OG/Twitter
 * tags, and optional JSON-LD structured data (e.g., FAQPage / BreadcrumbList).
 * Restores the previous values on unmount.
 *
 * @param {Object} opts
 * @param {string} opts.title
 * @param {string} opts.description
 * @param {string} opts.canonical
 * @param {Object} opts.og          - { title, description, url, image, type, siteName }
 * @param {Object} opts.twitter     - { card, title, description, image }
 * @param {Object|Array} opts.jsonLd
 */
export default function useSEO({
  title,
  description,
  canonical,
  og,
  twitter,
  jsonLd,
}) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    const applied = [];

    const applyMeta = (selector, attrName, attrValue, content) => {
      if (content == null || content === "") return;
      let el = document.querySelector(selector);
      const created = !el;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attrName, attrValue);
        document.head.appendChild(el);
      }
      const prev = el.getAttribute("content") || "";
      el.setAttribute("content", content);
      applied.push({ el, prev, created });
    };

    const applyLink = (rel, href) => {
      if (!href) return;
      let el = document.querySelector(`link[rel="${rel}"]`);
      const created = !el;
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      const prev = el.getAttribute("href") || "";
      el.setAttribute("href", href);
      applied.push({ el, prev, created, kind: "link" });
    };

    // Standard meta
    applyMeta('meta[name="description"]', "name", "description", description);

    // Canonical
    applyLink("canonical", canonical);

    // Open Graph
    if (og) {
      const map = {
        title: og.title,
        description: og.description,
        url: og.url,
        image: og.image,
        type: og.type || "website",
        site_name: og.siteName,
      };
      Object.entries(map).forEach(([key, value]) => {
        applyMeta(
          `meta[property="og:${key}"]`,
          "property",
          `og:${key}`,
          value
        );
      });
    }

    // Twitter
    if (twitter) {
      const map = {
        card: twitter.card || "summary_large_image",
        title: twitter.title,
        description: twitter.description,
        image: twitter.image,
      };
      Object.entries(map).forEach(([key, value]) => {
        applyMeta(
          `meta[name="twitter:${key}"]`,
          "name",
          `twitter:${key}`,
          value
        );
      });
    }

    // JSON-LD
    const scripts = [];
    const blocks = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];
    blocks.forEach((block) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.text = JSON.stringify(block);
      s.setAttribute("data-gd-seo", "1");
      document.head.appendChild(s);
      scripts.push(s);
    });

    return () => {
      document.title = prevTitle;
      applied.forEach(({ el, prev, created, kind }) => {
        if (created) {
          el.remove();
        } else if (kind === "link") {
          el.setAttribute("href", prev);
        } else {
          el.setAttribute("content", prev);
        }
      });
      scripts.forEach((s) => s.remove());
    };
  }, [title, description, canonical, og, twitter, jsonLd]);
}
